import { createTRPCClient, httpBatchLink, TRPCClientError, type TRPCClient } from '@trpc/client';
import type { AppRouter } from '@ama/api';
import { localAuthEnabled, localEmailAtual } from './localAuth';

/**
 * Cliente tRPC tipado. O token de sessão do Clerk é injetado por requisição
 * via `getToken` (registrado em runtime após o ClerkProvider montar).
 */
export interface TokenOptions {
  skipCache?: boolean;
}

let tokenProvider: (opts?: TokenOptions) => Promise<string | null> = async () => null;

export function setTokenProvider(fn: (opts?: TokenOptions) => Promise<string | null>) {
  tokenProvider = fn;
}

export const trpc: TRPCClient<AppRouter> = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: '/trpc',
      async headers() {
        const h: Record<string, string> = {};
        const token = await tokenProvider();
        if (token) h.Authorization = `Bearer ${token}`;
        // Modo local (sem Clerk): manda o e-mail logado para o backend decidir
        // super-admin pela allowlist (nunca concede admin sozinho) e derivar a
        // org — 100% automática, sem passo de criação/seleção (ver context.ts).
        if (localAuthEnabled()) {
          const email = localEmailAtual();
          if (email) h['x-dev-email'] = email;
        }
        return h;
      },
    }),
  ],
});

export function isTRPCError(err: unknown): err is TRPCClientError<AppRouter> {
  return err instanceof TRPCClientError;
}

/** Upload de planilha (multipart) - fora do tRPC, com o mesmo bearer token. */
export async function uploadPlanilha(file: File): Promise<UploadResponse> {
  const form = new FormData();
  form.append('arquivo', file);
  const doFetch = (token: string | null) => {
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    // Modo local (sem Clerk): mesmo header que o cliente tRPC manda — o
    // backend deriva a MESMA org (org_local_<e-mail>) para o upload cru e
    // para a confirmação via tRPC (ver index.ts). Sem isso os dois caminhos
    // resolviam orgs diferentes e a confirmação nunca achava a importação.
    if (localAuthEnabled()) {
      const email = localEmailAtual();
      if (email) headers['x-dev-email'] = email;
    }
    return fetch('/api/importacoes/upload', { method: 'POST', headers, body: form });
  };

  let res = await doFetch(await tokenProvider());
  if (res.status === 401) {
    // Token de sessão do Clerk dura ~60s e o getToken() usa cache: após a aba
    // dormir, o primeiro upload pode sair com token expirado. Renova
    // (skipCache) e repete UMA vez antes de mostrar erro.
    const fresh = await tokenProvider({ skipCache: true });
    res = await doFetch(fresh);
  }

  const json = (await res.json()) as UploadResponse;
  if (!res.ok && !json.importacaoId) {
    // erro de upload (formato/arquivo/auth): backend devolve { erro, code }
    throw new UploadError(json as unknown as { erro?: string; code?: string });
  }
  return json;
}

export class UploadError extends Error {
  code?: string;
  constructor(payload: { erro?: string; code?: string }) {
    super(payload.erro ?? 'Falha no upload.');
    this.code = payload.code;
    this.name = 'UploadError';
  }
}

// ── Tipos do payload de upload (espelham UploadResult do backend) ──────────
export interface ColumnMapping {
  coluna: string;
  campo: string | null;
  confianca: number;
}
export interface Pendencia {
  tipo: string;
  mensagem: string;
  linha?: number;
}
/** Prévia/resultado da re-importação incremental (espelha IncrementalResumo do backend). */
export interface IncrementalResumo {
  novos: number;
  atualizados: number;
  semMudanca: number;
  ausentes: number;
  divergencias: number;
  parcelasPagas: number;
  parcelasNovas: number;
  contatosPreenchidos: number;
  ausentesLista: { contratoId: string; unidade: string; codigo: string | null }[];
  divergenciasLista: { unidade: string; codigo: string | null; campo: string; mensagem: string }[];
}
export interface UploadResponse {
  importacaoId: string;
  status: 'processando' | 'revisao' | 'concluida' | 'erro';
  nomeArquivo: string;
  headers: string[];
  mapeamento: ColumnMapping[];
  camposNaoEncontrados: string[];
  formato: { format: string; confidence: number; reason: string; wideGroups: string[] };
  pendencias: Pendencia[];
  resumo: { contratos: number; compradores: number; parcelas: number };
  incremental: IncrementalResumo;
  requiredMissing: string[];
  preview: unknown[];
  erro?: string;
}
